import dayjs from "dayjs";

import React, { useCallback } from "react";

import { Box, Typography } from "@mui/material";

import { AIRLINE_IMG, AIRLINE_NAME_MAP } from "@/constants/airline";
import { AIRPORT_MAP } from "@/constants/airport";
import usePlatform from "@/hooks/usePlatform";
import { FlightItem, Product } from "@/typings";

interface ProductInfoProps {
  product: Product;
  data: FlightItem;
  isLast: boolean;
  showCabin?: boolean;
}

const AirlineDetail = ({
  product,
  showCabin = true,
}: {
  product: Product;
  showCabin?: boolean;
}) => {
  const { isMobile } = usePlatform();

  if (isMobile) {
    return (
      <div className="flex flex-col">
        <Typography variant="caption" color="textSecondary">
          {AIRLINE_NAME_MAP[product.airline_code]}

          {showCabin && ` · ${product.cabin_type}`}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {product.aircraft}
          {` · `}
          {product.flight_number}
        </Typography>
      </div>
    );
  }

  return (
    <Typography variant="caption" color="textSecondary">
      {AIRLINE_NAME_MAP[product.airline_code]}
      {` · `}
      {showCabin && `${product.cabin_type} · `}
      {product.aircraft}
      {` · `}
      {product.flight_number}
    </Typography>
  );
};

const ProductInfo = ({ product, data, isLast, showCabin = true }: ProductInfoProps) => {
  const airportDisplay = (airport_code: string) => {
    return AIRPORT_MAP[airport_code] 
      ? `${AIRPORT_MAP[airport_code].name} (${airport_code})`
      : `${airport_code} (${airport_code})`;
  };

  const formatTime = useCallback((minutes: number) => {
    if (minutes > 60) {
      return `${Math.floor(minutes / 60)} hr ${minutes % 60} min`;
    } else {
      return `${minutes} min`;
    }
  }, []);

  return (
    <div className="pl-[30px]">
      <div className="relative">
        <div className="flex">
          <Typography>{dayjs(product.departure_time).format("hh:mmA")}</Typography>
          <Typography className="px-[5px]">·</Typography>
          <Typography>{airportDisplay(product.origin)}</Typography>
        </div>

        <Typography color="textSecondary" variant="body2" className="py-[12px]">
          Travel time: {formatTime(product.travel_minutes)}
        </Typography>

        <div className="flex">
          <Typography>{dayjs(product.arrival_time).format("hh:mmA")}</Typography>
          <Typography className="px-[5px]">·</Typography>
          <Typography>{airportDisplay(product.destination)}</Typography>
        </div>

        <div className="absolute w-[40px] left-[0px] translate-x-[-100%] top-0 bottom-0 flex items-center justify-center">
          <div
            className="w-[12px] h-[12px] rounded-[50%] absolute top-[6px] box-border"
            style={{ border: "2px solid #5f6368" }}
          />
          <div className="absolute top-[20px] bottom-[20px] overflow-hidden">
            {[...Array(18).keys()].map((ele) => (
              <div key={`${ele}`} className="w-[4px] h-[4px] rounded-[50%] bg-[#dadce0] my-[4px]" />
            ))}
          </div>
          <div
            className="w-[12px] h-[12px] rounded-[50%] absolute bottom-[6px] box-border"
            style={{ border: "2px solid #5f6368" }}
          />
        </div>
      </div>

      <div className="pt-[18px] flex items-center">
        {/* <Box
          component="img"
          className="max-h-[22px] w-[22px] mr-[10px]"
          alt={data.airline_code}
          src={AIRLINE_IMG[product.airline_code]}
        /> */}

        <AirlineDetail product={product} showCabin={showCabin} />
      </div>

      {!isLast ? (
        <Typography
          sx={{
            padding: "16px 0",
            margin: "20px 0",
            borderTop: "1px solid",
            borderBottom: "1px solid",
            borderColor: (theme) => theme.palette.divider,
          }}
        >
          {formatTime(product.layover_time)}
          {` layover · `}

          {airportDisplay(product.destination)}
          {/* {product.destination} */}
        </Typography>
      ) : null}
    </div>
  );
};

export default React.memo(ProductInfo);
